import { API_BASE_URL } from '../config/constants';

export interface BackofficeLoginRequest {
  email: string;
  password: string;
  ip_address?: string;
}

export interface BackofficeUser {
  id: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  last_login_at?: string;
}

export interface BackofficeLoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: BackofficeUser;
}

export interface BackofficeTokenRefresh {
  refresh_token: string;
  access_token?: string;
  token_type: string;
  expires_in?: number;
}

class BackofficeAuthService {
  private baseUrl = `${API_BASE_URL}/backoffice/auth`;

  async login(credentials: BackofficeLoginRequest): Promise<BackofficeLoginResponse> {
    const response = await fetch(`${this.baseUrl}/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...credentials,
        ip_address: await this.getClientIP()
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Login failed');
    }

    const data = await response.json();
    
    // Store tokens
    this.setTokens(data.access_token, data.refresh_token);
    
    return data;
  }

  async refreshToken(): Promise<BackofficeTokenRefresh> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await fetch(`${this.baseUrl}/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refresh_token: refreshToken,
      }),
    });

    if (!response.ok) {
      this.logout();
      throw new Error('Token refresh failed');
    }

    const data = await response.json();
    this.setAccessToken(data.access_token);
    
    return data;
  }

  async getCurrentUser(): Promise<BackofficeUser> {
    const token = this.getAccessToken();
    if (!token) {
      throw new Error('No access token available');
    }

    const response = await fetch(`${this.baseUrl}/me`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        // Try to refresh token
        try {
          await this.refreshToken();
          return this.getCurrentUser();
        } catch {
          this.logout();
          throw new Error('Session expired');
        }
      }
      throw new Error('Failed to get user info');
    }

    return response.json();
  }

  async logout(): Promise<void> {
    const token = this.getAccessToken();
    
    try {
      if (token) {
        await fetch(`${this.baseUrl}/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      }
    } catch (error) {
      console.warn('Logout request failed:', error);
    } finally {
      this.clearTokens();
    }
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const token = this.getAccessToken();
    if (!token) {
      throw new Error('No access token available');
    }

    const response = await fetch(`${this.baseUrl}/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Password change failed');
    }
  }

  // Token management
  private setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem('backoffice_access_token', accessToken);
    localStorage.setItem('backoffice_refresh_token', refreshToken);
  }

  private setAccessToken(accessToken: string): void {
    localStorage.setItem('backoffice_access_token', accessToken);
  }

  getAccessToken(): string | null {
    return localStorage.getItem('backoffice_access_token');
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem('backoffice_refresh_token');
  }

  private clearTokens(): void {
    localStorage.removeItem('backoffice_access_token');
    localStorage.removeItem('backoffice_refresh_token');
  }

  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  private async getClientIP(): Promise<string | undefined> {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch {
      return undefined;
    }
  }
}

export const backofficeAuthService = new BackofficeAuthService();